import React, { useState } from "react";
import { Group, Text, UnstyledButton } from "@mantine/core";
import { IconChevronRight, IconUser } from "@tabler/icons-react";
import { Client } from "../../types/client";
import { ClientDetailTab } from "./ClientDetails";
import ClientDetailsDrawer from "./ClientDetailsDrawer";

interface ClientLinkButtonProps {
  client: Client;
  /** Tab to open the drawer on (e.g. "notes", "overview"). */
  initialTab?: ClientDetailTab;
  /** Brand teal by default; overridable for dense/dark surfaces. */
  iconColor?: string;
  /** Mantine text size for the name. */
  size?: string;
  /**
   * Runs immediately before the drawer opens — used by the calendar popover to
   * close itself first so the full-width drawer replaces it rather than
   * stacking behind it.
   */
  onBeforeOpen?: () => void;
}

/**
 * Clickable client name that opens the ClientDetailsDrawer. Shared by the
 * Transaction Modal and the calendar appointment drawer so the link + drawer
 * wiring lives in one place.
 */
const ClientLinkButton: React.FC<ClientLinkButtonProps> = ({
  client,
  initialTab,
  iconColor = "var(--brand-teal)",
  size = "sm",
  onBeforeOpen,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpen = () => {
    onBeforeOpen?.();
    setDrawerOpen(true);
  };

  return (
    <>
      <UnstyledButton onClick={handleOpen}>
        <Group gap={4} wrap="nowrap">
          <IconUser size={16} color={iconColor} />
          <Text
            size={size}
            fw={500}
            c="var(--brand-teal)"
            style={{
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 3,
            }}
          >
            {client.first_name} {client.last_name}
          </Text>
          <IconChevronRight size={16} color={iconColor} />
        </Group>
      </UnstyledButton>

      <ClientDetailsDrawer
        client={client}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initialTab={initialTab}
        hideDeleteButton
      />
    </>
  );
};

export default ClientLinkButton;
